import { z } from 'zod';
import { parseLocalDate } from '../local-date.js';

/**
 * Sertifika ve klas formlarının ortak alan şemaları (M7d). Hata iletisi koddur; arayüz çevirir.
 * Ekipman kartının yardımcılarıyla aynı davranış: boş metin `null`, eksik zorunlu alan `REQUIRED`.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

export const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

export const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

/** Çok satırlı not: satır sonları korunur, boş satırlar atılır. */
export const optionalLines = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) =>
      value
        .normalize('NFC')
        .split(/\r\n|\r|\n/)
        .map(collapse)
        .filter((line) => line !== '')
        .join('\n'),
    )
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

export const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));

export const flag = () =>
  z
    .boolean(typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? false);

export const integer = (min: number, max: number) =>
  z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(min, 'OUT_OF_RANGE')
    .max(max, 'OUT_OF_RANGE');

export const localDate = z.string(typed('INVALID_DATE')).refine((value) => {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}, 'INVALID_DATE');

export const optionalDate = localDate.nullish().transform((value) => value ?? null);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const uuid = z.string(typed('INVALID_ID')).regex(UUID, 'INVALID_ID');
export const optionalUuid = uuid.nullish().transform((value) => value ?? null);
